import type { EndpointMap } from './Types';

enum ModelNamespace {
    Auth = 'auth/',
    Portal = 'portal/',
    Displays = 'displays/',
    InternalAdmin = 'internaladmin/',
    PopDisplays = 'popdisplays/',
    Reporting = 'reporting/',
    AI = 'ai/',
}

const createGeneric = (base: string, namespace: ModelNamespace) => ({
    list: `${namespace}${base}/list`,
    cursor: `${namespace}${base}/cursor`,
    create: `${namespace}${base}/create`,
    get: `${namespace}${base}/:id`,
    update: `${namespace}${base}/:id`,
    delete: `${namespace}${base}/:id`,
});

export const DEFAULT_ENDPOINT_MAP: EndpointMap = {
    Entities: {
        Agency: createGeneric('agency', ModelNamespace.Portal),
        Alert: {
            ...createGeneric('alert', ModelNamespace.Portal),
            list: `${ModelNamespace.Portal}alerts`,
        },
        Buyer: {
            ...createGeneric('buyer', ModelNamespace.Portal),
            attachAudienceVersion: `${ModelNamespace.Portal}buyer/:id/attach-audience-source`,
        },
        BuyerCost: createGeneric('buyercost', ModelNamespace.Portal),
        Campaign: {
            ...createGeneric('campaign', ModelNamespace.Portal),
            lineItems: `${ModelNamespace.Portal}campaign/:id/line-items`,
            lineItemStatus: `${ModelNamespace.Portal}campaign/:id/line-items/status`,
            download: `${ModelNamespace.Portal}campaign/:id/download`,
            shareUrl: `${ModelNamespace.Portal}campaign/:id/share`,
            unShareUrl: `${ModelNamespace.Portal}campaign/:id/unshare`,
            syncLineItems: `${ModelNamespace.Portal}campaign/:id/line-items/sync`,
            overrideBudgetSummary: `${ModelNamespace.Portal}campaign/:id/summary/override`,
            setNotices: `${ModelNamespace.Portal}campaign/:id/notices`,
            resetNotices: `${ModelNamespace.Portal}campaign/:id/reset-data-status`,
            downloadPCA: `${ModelNamespace.Portal}campaign/:id/pca-download`,
            archive: `${ModelNamespace.Portal}campaign/:id/archive`,
            creatives: `${ModelNamespace.Portal}campaign/:id/creatives`,

            // UL-7912 -- DSP insertion order targeting. Read-only catalogue lives under
            // popdisplays; everything campaign-scoped hangs off the campaign like campaign-media.
            insertionOrderCatalogue: `${ModelNamespace.PopDisplays}insertion-orders/list`,
            insertionOrders: `${ModelNamespace.Portal}campaign/:id/insertion-orders`,
            insertionOrderRules: `${ModelNamespace.Portal}campaign/:id/insertion-orders/rules`,
            insertionOrderRule: `${ModelNamespace.Portal}campaign/:id/insertion-orders/rules/:ruleId`,
            insertionOrderRulesBatch: `${ModelNamespace.Portal}campaign/:id/insertion-orders/rules/batch`,
            insertionOrderDetail: `${ModelNamespace.Portal}campaign/:id/insertion-orders/detail`,
            insertionOrderSync: `${ModelNamespace.Portal}campaign/:id/insertion-orders/sync`,
            insertionOrderSchedule: `${ModelNamespace.Portal}campaign/:id/insertion-orders/schedule`,
            // UL-8136 -- planned lines: a target and schedule before the real insertion order exists.
            insertionOrderPlaceholders: `${ModelNamespace.Portal}campaign/:id/insertion-orders/placeholders`,
            insertionOrderPlaceholder: `${ModelNamespace.Portal}campaign/:id/insertion-orders/placeholders/:placeholderId`,
            insertionOrderPlaceholderLink: `${ModelNamespace.Portal}campaign/:id/insertion-orders/placeholders/:placeholderId/link`,
            allowedVenues: `${ModelNamespace.Portal}campaign/:id/allowed-venues`,
            allowedVenuesUpload: `${ModelNamespace.Portal}campaign/:id/allowed-venues/upload`,
            autoGenerateCreativeGroups: `${ModelNamespace.Portal}campaign/:id/validate-creative-groups`,
            createAutoGeneratedGroups: `${ModelNamespace.Portal}campaign/:id/assign-creative-groups`,
            forceAssignCreativesToLineItems: `${ModelNamespace.Portal}campaign/:id/force-auto-assign`,
            forceUnAssignCreativesFromLineItems: `${ModelNamespace.Portal}campaign/:id/unassign`,
            autoAssignCreativesToLineItems: `${ModelNamespace.Portal}campaign/:id/auto-assign`,
            massUnAssignCreativesFromLineItems: `${ModelNamespace.Portal}campaign/:id/mass-unassign`,
            pcaExport: `${ModelNamespace.Portal}campaign/:id/pca-raw-export`,
            pcaImport: `${ModelNamespace.Portal}campaign/:id/pca-raw-import`,

            displayIssues: `${ModelNamespace.Portal}campaign/:id/issues`,
            errors: `${ModelNamespace.Portal}campaign/:id/errors`,
            validate: `${ModelNamespace.Portal}campaign/:id/validate`,
            uploadBookingSummary: `${ModelNamespace.Portal}campaign/:id/booking-summary/upload`,
            creativeValidation: `${ModelNamespace.Portal}campaign/:id/creative-validation`,
            deleteBookingSummary: `${ModelNamespace.Portal}campaign/:id/booking-summary/:bookingSummary`,
            deleteLegacyBookingSummary: `${ModelNamespace.Portal}campaign/:id/old-booking-summary`,
            syncContacts: `${ModelNamespace.Portal}campaign/:id/additional-contacts/sync`,
            syncMedia: `${ModelNamespace.Portal}campaign/:id/campaign-media/sync`,
            updatePopSettings: `${ModelNamespace.Portal}campaign/:id/pop-settings`,
            popSettingsMediaTable: `${ModelNamespace.Portal}campaign/:id/campaign-media/list`,
            mediaTableParameter: `${ModelNamespace.Portal}campaign/:id/campaign-media/parameter`,
            deleteMediaTableParameter: `${ModelNamespace.Portal}campaign/:campaignId/campaign-media/parameter/:id`,
            mediaAutoGroup: `${ModelNamespace.Portal}campaign/:id/campaign-media/auto-group`,
            mediaGroupParameter: `${ModelNamespace.Portal}campaign/:id/campaign-media/group-parameter`,
            campaignMediaGroups: `${ModelNamespace.Portal}campaign/:id/campaign-media/groups`,
            removeIdsFromMediaGroup: `${ModelNamespace.Portal}campaign/:id/campaign-media/group/remove`,
            CampaignMediaList: `${ModelNamespace.Portal}campaign/:id/classic/campaign-media`,
            deleteCampaignMedia: `${ModelNamespace.Portal}campaign/:id/classic/campaign-media/delete`,
            editGroup: `${ModelNamespace.Portal}campaign/:id/campaign-media/group`,
            auditEvent: `${ModelNamespace.Portal}campaign/:id/audit`,
            scheduleLineItems: `${ModelNamespace.Portal}campaign/:id/schedule/line-items`,
            screenFromSchedule: `${ModelNamespace.Portal}campaign/:id/schedule/line-items/:lineItemId/displays`,
            schedules: `${ModelNamespace.Portal}campaign/:id/schedule`,
            scheduleRules: `${ModelNamespace.Portal}campaign/:id/schedule/:scheduleId/rules`,
            attachSchedules: `${ModelNamespace.Portal}campaign/:id/schedule/attach`,
            detachSchedules: `${ModelNamespace.Portal}campaign/:id/schedule/detach`,
            deleteSchedules: `${ModelNamespace.Portal}campaign/:id/schedule/:scheduleId`,
            listTargets: `${ModelNamespace.Portal}campaign/:id/targets/list`,
            updateTargets: `${ModelNamespace.Portal}campaign/:id/targets/:targetId`,
            createTargets: `${ModelNamespace.Portal}campaign/:id/targets`,
            deleteTargets: `${ModelNamespace.Portal}campaign/:id/targets/:targetId`,
            attachTargetToSchedules: `${ModelNamespace.Portal}campaign/:id/targets/:targetId/attach`,
            detachTargetFromSchedules: `${ModelNamespace.Portal}campaign/:id/targets/:targetId/detach`,
            campaignPowerpoint: `${ModelNamespace.Portal}campaign/:id/powerpoint`,
            campaignPdfSend: `${ModelNamespace.Portal}campaign/:id/pdf/send`,
            campaignPowerpointSend: `${ModelNamespace.Portal}campaign/:id/powerpoint/send`,
            channelFrame: `${ModelNamespace.Portal}campaign/:id/channel-frame/queue-report`,
            addDisplay: `${ModelNamespace.Portal}campaign/:id/schedule/displays/add`,
            removeDisplay: `${ModelNamespace.Portal}campaign/:id/schedule/displays/remove`,
            removeUnknownDisplays: `${ModelNamespace.Portal}campaign/:id/schedule/displays/remove-unknown`,
            listDisplays: `${ModelNamespace.Portal}campaign/:id/schedule/displays`,
            listPopDisplays: `${ModelNamespace.Portal}campaign/:id/schedule/pop-line-items`,
            displaysDownload: `${ModelNamespace.Portal}campaign/:id/schedule/displays/download`,
            listUnknownDisplays: `${ModelNamespace.Portal}campaign/:id/schedule/unknown-displays`,
            reImportUnknownDisplays: `${ModelNamespace.Portal}campaign/:id/schedule/reimport-unknown-displays`,
            downloadUnknownDisplays: `${ModelNamespace.Portal}campaign/:id/schedule/unknown-displays/download`,
        },
        Pca: createGeneric('pca', ModelNamespace.Displays),
        CampaignDisplay: createGeneric('campaigndisplay', ModelNamespace.Portal),
        CampaignInstruction: createGeneric('campaigninstruction', ModelNamespace.Portal),
        CampaignReporting: {
            ...createGeneric('campaign-reporting', ModelNamespace.Portal),
            attach: `${ModelNamespace.Portal}campaign-reporting/:id/attach`,
        },
        CampaignOwnerContactedDate: createGeneric('campaignownercontacted', ModelNamespace.Portal),
        CampaignPopShot: {
            ...createGeneric('campaignpopshot', ModelNamespace.Portal),
            update: `${ModelNamespace.Portal}campaignpopshot`,
        },
        Creative: {
            ...createGeneric('creative', ModelNamespace.Portal),
            download: `${ModelNamespace.Portal}creative/download`,
            update: `${ModelNamespace.Portal}creative`,
            delete: `${ModelNamespace.Portal}creative`,
        },
        CreativeGroup: {
            ...createGeneric('creativegroup', ModelNamespace.Portal),
            delete: `${ModelNamespace.Portal}creativegroup`,
        },
        Client: createGeneric('client', ModelNamespace.Portal),
        Date: createGeneric('date', ModelNamespace.Portal),
        Display: createGeneric('display', ModelNamespace.Portal),
        File: createGeneric('file', ModelNamespace.Portal),
        Market: createGeneric('market', ModelNamespace.Portal),
        MissingDisplay: createGeneric('missingdisplay', ModelNamespace.InternalAdmin),
        Note: createGeneric('note', ModelNamespace.Portal),
        NotificationReport: createGeneric('reports', ModelNamespace.Portal),
        Owner: createGeneric('owner', ModelNamespace.Portal),
        Pack: createGeneric('pack', ModelNamespace.Portal),
        Permission: createGeneric('permission', ModelNamespace.Auth),
        Post: createGeneric('post', ModelNamespace.Portal),
        Request: createGeneric('request', ModelNamespace.Portal),
        Resolution: createGeneric('resolution', ModelNamespace.Portal),
        Role: {
            ...createGeneric('role', ModelNamespace.Auth),
            updatePermissions: `${ModelNamespace.Auth}role/:id/permissions`,
        },
        DataStatusLineItems: createGeneric('line-items-data-status', ModelNamespace.Portal),
        Rule: createGeneric('rule', ModelNamespace.Portal),
        Schedule: {
            ...createGeneric('schedule', ModelNamespace.Portal),
            createInCampaign: `${ModelNamespace.Portal}campaign/:campaign/schedule`,
            updateInCampaign: `${ModelNamespace.Portal}campaign/:campaign/schedule/:id`,
            campaignOwner: `${ModelNamespace.Portal}campaignowner/:id`,

            delete: `${ModelNamespace.Portal}schedule`,

            campaignSchedules: `${ModelNamespace.Portal}campaign/:campaign/schedule/list`,
            deleteRules: `${ModelNamespace.Portal}campaign/schedule/:id/rules`,

            uploadSiteList: `${ModelNamespace.Portal}campaign/:campaign/schedule/upload`,
            downloadSite: `${ModelNamespace.Portal}campaign/:campaign/schedule/:id/download`,
            revalidateSite: `${ModelNamespace.Portal}campaign/:campaign/schedule/:id/revalidate`,
        },
        Skin: {
            ...createGeneric('skin', ModelNamespace.Portal),
            uploadLargeLogo: `${ModelNamespace.Portal}skin/:id/logo`,
            uploadSmallLogo: `${ModelNamespace.Portal}skin/:id/logosmall`,
            uploadLargeDarkLogo: `${ModelNamespace.Portal}skin/:id/logodark`,
            uploadSmallDarkLogo: `${ModelNamespace.Portal}skin/:id/logosmalldark`,
            attach: `${ModelNamespace.Portal}skin/:id/attach`,
        },
        SpotlightRecentResource: {},
        Stream: createGeneric('stream', ModelNamespace.Portal),
        User: {
            ...createGeneric('user', ModelNamespace.Portal),
            changeMyPassword: `${ModelNamespace.Portal}user/me/change-password`,
            me: `${ModelNamespace.Portal}user/me`,
            updateMe: `${ModelNamespace.Portal}user/me`,
            updateFavourites: `${ModelNamespace.Portal}user/:user/favourites`,
            resendInvitation: `${ModelNamespace.Portal}user/:id/resend-invitation`,
        },

        Displays_AnalogueScreen: {
            ...createGeneric('analoguescreen', ModelNamespace.Displays),
            brochure: `${ModelNamespace.Displays}analoguescreen/:id/brochure`,
        },
        Displays_AudienceSource: createGeneric('audiencesource', ModelNamespace.Displays),
        Displays_AudienceFallback: createGeneric('audience-fallback', ModelNamespace.Displays),
        Displays_AudienceUpload: {
            ...createGeneric('audience', ModelNamespace.Displays),
            uploadManualAudience: `${ModelNamespace.Displays}audience/upload`,
        },
        Displays_AudienceVersion: createGeneric('audienceversion', ModelNamespace.Displays),
        Displays_Tag: {
            ...createGeneric('tag', ModelNamespace.Displays),
            createOperationalTimes: `${ModelNamespace.Displays}tag/:id/operational-times`,
            destroyOperationalTime: `${ModelNamespace.Displays}tag/:id/operational-times/:oid`,
        },
        Displays_Country: createGeneric('country', ModelNamespace.Displays),
        Displays_Currency: createGeneric('currency', ModelNamespace.Displays),
        Displays_DisplayIssues: createGeneric('issues', ModelNamespace.Displays),
        Displays_FileType: createGeneric('filetype', ModelNamespace.Displays),
        Displays_FrameType: createGeneric('frametype', ModelNamespace.Displays),
        Displays_Market: createGeneric('market', ModelNamespace.Displays),
        Displays_OperationalTime: createGeneric('operationaltime', ModelNamespace.Displays),
        Displays_Owner: {
            ...createGeneric('owner', ModelNamespace.Displays),
            getOwner: `${ModelNamespace.Displays}owner/get-owner`,
        },
        Displays_Pack: {
            ...createGeneric('pack', ModelNamespace.Displays),
            createOperationalTimes: `${ModelNamespace.Displays}pack/:id/operational-times`,
            destroyOperationalTime: `${ModelNamespace.Displays}pack/:id/operational-times/:oid`,
            brochure: `${ModelNamespace.Displays}pack/:id/brochure`,
        },
        Displays_Partner: createGeneric('partner', ModelNamespace.Displays),
        Displays_Screen: {
            ...createGeneric('screen', ModelNamespace.Displays),
            mediaBreakdown: `${ModelNamespace.Displays}screen/:id/media`,
            createOperationalTimes: `${ModelNamespace.Displays}screen/:id/operational-times`,
            destroyOperationalTime: `${ModelNamespace.Displays}screen/:id/operational-times/:oid`,
            attachVendorIds: `${ModelNamespace.Displays}screen/:id/attachDisplaysVendorIds`,
            deleteVendorIds: `${ModelNamespace.Displays}screen/:screenId/vendor/:vendorId/delete`,
            listVendorIds: `${ModelNamespace.Displays}screen/vendor/vendorName/list`,
            listEnvironments: `${ModelNamespace.Displays}screen/environment/list`,
            brochure: `${ModelNamespace.Displays}screen/:id/brochure`,
            frameSearch: `${ModelNamespace.Displays}screen/frame-search `,
            screenAllocation: `${ModelNamespace.Displays}screen/allocation`,
            exportCsv: `${ModelNamespace.Displays}screen/export`,
            reImport: `${ModelNamespace.Displays}screen/re-import`,
            massUpdating: `${ModelNamespace.Displays}screen/import`,
            importOperationalTimes: `${ModelNamespace.Displays}screen/operational-times/import`,
            getScreens: `${ModelNamespace.Displays}screen/get-screen`,
            addChannelFrame: `${ModelNamespace.Displays}screen/:id/add-channel-frame`,
            removeChannelFrame: `${ModelNamespace.Displays}screen/:id/remove-channel-frame`,
        },
        Displays_LineItem: {
            ...createGeneric('lineitem', ModelNamespace.Displays),
            validateFrameIds: `${ModelNamespace.Displays}lineitem/frameids`,
            frameDownload: `${ModelNamespace.Displays}screen/download/resolutions`,
        },
        Displays_Vendor: createGeneric('vendor', ModelNamespace.Displays),

        PopDisplays_ChannelFrame: {
            ...createGeneric('channelframe', ModelNamespace.PopDisplays),
            mediaBreakdown: `${ModelNamespace.PopDisplays}channelframe/:id/media`,
            export: `${ModelNamespace.PopDisplays}channelframe/export`,
            import: `${ModelNamespace.PopDisplays}channelframe/import`,
            addDisplayPortalId: `${ModelNamespace.PopDisplays}channelframe/:id/add-display`,
            removeDisplayPortalId: `${ModelNamespace.PopDisplays}channelframe/:id/remove-display`,
        },
        PopDisplays_Display: {
            ...createGeneric('display', ModelNamespace.PopDisplays),
            export: `${ModelNamespace.PopDisplays}display/export`,
            mediaBreakdown: `${ModelNamespace.PopDisplays}display/:id/media`,
            attachChannelFrames: `${ModelNamespace.PopDisplays}displaychannelframe/:id/create`,
            detachChannelFrames: `${ModelNamespace.PopDisplays}displaychannelframe/:id`,
        },
        PopDisplays_GraphReportType: {
            ...createGeneric('graphreporttype', ModelNamespace.PopDisplays),
        },
        PopDisplays_JobStatus: {
            ...createGeneric('jobstatus', ModelNamespace.PopDisplays),
        },
        PopDisplays_LogFileImport: {
            ...createGeneric('logfileimport', ModelNamespace.PopDisplays),
            upload: `${ModelNamespace.PopDisplays}logfileimport/upload`,
            changeStatus: `${ModelNamespace.PopDisplays}logfileimport/change-status`,
            classicUpload: `${ModelNamespace.PopDisplays}logfileimport/classic/upload`,
        },
        PopDisplays_LogFileImportSummary: {
            summary: `${ModelNamespace.PopDisplays}logfileimport/:id/summary`,
        },
        PopDisplays_LogFileParsed: {
            ...createGeneric('logfileparsed', ModelNamespace.PopDisplays),
        },
        PopDisplays_LogFileResource: {
            ...createGeneric('logfileresource', ModelNamespace.PopDisplays),
        },
        PopDisplays_LogImportSource: {
            ...createGeneric('logimportsource', ModelNamespace.PopDisplays),
        },
        PopDisplays_LogSourceAccount: {
            ...createGeneric('logsourceaccount', ModelNamespace.PopDisplays),
        },
        PopDisplays_LogSourceCampaignIdentifier: {
            ...createGeneric('logsourcecampaignidentifier', ModelNamespace.PopDisplays),
        },
        PopDisplays_Market: {
            ...createGeneric('market', ModelNamespace.PopDisplays),
        },
        PopDisplays_Media: {
            ...createGeneric('media', ModelNamespace.PopDisplays),
        },
        Campaign_Chat: {
            ...createGeneric('chat', ModelNamespace.AI),
            chatEndpoint: `${ModelNamespace.AI}chat`,
        },
    },
    auth: {
        login: `${ModelNamespace.Auth}login`,
        refresh: `${ModelNamespace.Auth}refresh`,
        logout: `${ModelNamespace.Auth}logout`,
        requestForgotPassword: `${ModelNamespace.Auth}forgot-password`,
        resetPassword: `${ModelNamespace.Auth}reset-password`,
        revalidate: `${ModelNamespace.Auth}revalidate-email`,
        startImpersonating: `${ModelNamespace.Auth}impersonate/:id`,
        stopImpersonating: `${ModelNamespace.Auth}impersonate/stop`,

        // Not under the auth namespace: the OAuth routes are their own group on the API
        // (routes/api/oauth.php), mounted so the protocol endpoints sit outside /v1.
        approveMcpAuthorization: 'oauth/authorize',

        inviteDetails: `${ModelNamespace.Portal}invite/detail`,
        inviteAccept: `${ModelNamespace.Portal}invite/accept`,
        register: `${ModelNamespace.Portal}user/register`,
        verifyEmail: `${ModelNamespace.Portal}user/verify`,
    },
    usage: {
        summary: `${ModelNamespace.InternalAdmin}usage/summary`,
        organisations: `${ModelNamespace.InternalAdmin}usage/organisations`,
        organisation: `${ModelNamespace.InternalAdmin}usage/organisations/:type/:id`,
        user: `${ModelNamespace.InternalAdmin}usage/users/:id`,
        features: `${ModelNamespace.InternalAdmin}usage/features`,
    },
    notifications: {
        listReports: `${ModelNamespace.Portal}reports/list`,
        getReport: `${ModelNamespace.Portal}reports/:id`,
        createReport: `${ModelNamespace.Portal}reports/create`,
        previewReport: `${ModelNamespace.Portal}reports/:id/preview`,
        getReportRuns: `${ModelNamespace.Portal}reports/:id/runs`,
        createReportQuery: `${ModelNamespace.Portal}reportquery/create`,
        detectReportQueryColumns: `${ModelNamespace.Portal}reportquery/detect-columns`,
        getReportQueryDeltaCache: `${ModelNamespace.Portal}reportquery/:id/delta-cache`,
        clearReportQueryDeltaCache: `${ModelNamespace.Portal}reportquery/:id/delta-cache`,
        seedReportQueryDeltaBaseline: `${ModelNamespace.Portal}reportquery/:id/delta-cache/baseline`,
        updateReportQuery: `${ModelNamespace.Portal}reportquery/:id`,
        deleteReportQuery: `${ModelNamespace.Portal}reportquery/:id`,
    },
    happyDemics: `${ModelNamespace.Portal}happydemics/list`,
    reports: {
        dashboard: {
            charts: `${ModelNamespace.Reporting}dynamic`,
            campaignReports: `${ModelNamespace.Reporting}campaign`,
            live: `${ModelNamespace.Reporting}live`,
        },
        datasource: {
            list: `${ModelNamespace.Portal}datasources/list`,
            create: `${ModelNamespace.Portal}datasources/create`,
            detectColumns: `${ModelNamespace.Portal}datasources/detect-columns`,
            detail: `${ModelNamespace.Portal}datasources/:datasource`,
            sync: `${ModelNamespace.Portal}datasources/:datasource/sync`,
            syncs: `${ModelNamespace.Portal}datasources/:datasource/syncs`,
            fields: `${ModelNamespace.Reporting}datasource/:datasource/fields`,
            query: `${ModelNamespace.Reporting}datasource/:datasource/query`,
        },
        pca: {
            endpoint: `${ModelNamespace.Reporting}pcareport/:id/:report`,
            mapEndpoint: `${ModelNamespace.Reporting}pcareport-map/:id`,
            analyticsEndpoint: `${ModelNamespace.Reporting}pcareport-analytics/:id`,
            campaignFramesEndpoint: `${ModelNamespace.Reporting}pcareport-frames/:id`,
            energyEndpoint: `${ModelNamespace.Reporting}pcareport/:id/energy/:field`,
            run: `${ModelNamespace.Reporting}cadreport/:id/run`,
            delete: `${ModelNamespace.Reporting}cadreport/:id/delete`,
            targetMultiplier: `${ModelNamespace.Reporting}cadreport/:id/calculate-target-multiplier`,
            publish: `${ModelNamespace.Reporting}pcareport/:id/publish`,
            impressionMultipliers: `${ModelNamespace.Reporting}pcareport/:id/impression-multipliers`,
            geopathImpressionMultipliers: `${ModelNamespace.Reporting}pcareport/:id/geopath-impression-multipliers`,
            classicBookedLines: `${ModelNamespace.Reporting}pcareport/:id/classic-booked-lines`,
        },
        pcaElasticSearch: {
            summary: `${ModelNamespace.Reporting}pcareport/:id/summary`,
            dsp: `${ModelNamespace.Reporting}pcareport/:id/dsp`,
            groupByField: `${ModelNamespace.Reporting}pcareport/:id`,
            frameIdMapData: `${ModelNamespace.Reporting}pcareport/:id/map`,
            displayMapData: `${ModelNamespace.Reporting}pcareport/:id/map/:displayId`,
            stats: `${ModelNamespace.Reporting}pcareport/:id/stats`,
            histogram: `${ModelNamespace.Reporting}pcareport/:id/histogram`,
        },
    },
    virtualCampaignBuilder: {
        summary: `${ModelNamespace.PopDisplays}campaign/build`,
        standardPlaysExport: `${ModelNamespace.PopDisplays}campaign/export`,
        displayPlaysExport: `${ModelNamespace.PopDisplays}campaign/display-export`,
    },
    popDisplays: {
        playoutExport: `${ModelNamespace.PopDisplays}logfileparsed/playoutExport`,
    },
};
